/**
 * Keyboard Shortcuts Hook
 *
 * Manages global keyboard shortcuts for the application
 * Supports modifier keys (Cmd/Ctrl, Shift, Alt)
 */

import { useEffect, useCallback } from 'react';
import type { KeyboardShortcutDef, FormattedShortcut } from '../types';

// Platform detection
const isMac = typeof window !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

/**
 * Check if event matches shortcut
 */
const matchesShortcut = (event: KeyboardEvent, shortcut: KeyboardShortcutDef): boolean => {
  const { key, meta, ctrl, shift, alt } = shortcut;

  // Normalize the pressed key
  const pressedKey = event.key.toLowerCase();
  const targetKey = key.toLowerCase();

  // Check if key matches
  if (pressedKey !== targetKey) {
    return false;
  }

  // Check modifier keys
  const hasCtrlOrCmd = isMac ? event.metaKey : event.ctrlKey;
  const requiresCtrlOrCmd = isMac ? meta : ctrl;

  if (requiresCtrlOrCmd && !hasCtrlOrCmd) return false;
  if (!requiresCtrlOrCmd && hasCtrlOrCmd) return false;

  if (shift && !event.shiftKey) return false;
  if (!shift && event.shiftKey) return false;

  if (alt && !event.altKey) return false;
  if (!alt && event.altKey) return false;

  return true;
};

/**
 * Format shortcut for display
 */
export const formatShortcut = (shortcut: KeyboardShortcutDef): string => {
  const keys: string[] = [];

  if (isMac) {
    if (shortcut.meta) keys.push('⌘');
    if (shortcut.ctrl) keys.push('⌃');
    if (shortcut.alt) keys.push('⌥');
    if (shortcut.shift) keys.push('⇧');
  } else {
    if (shortcut.ctrl) keys.push('Ctrl');
    if (shortcut.alt) keys.push('Alt');
    if (shortcut.shift) keys.push('Shift');
  }

  keys.push(shortcut.key.toUpperCase());

  return keys.join(isMac ? '' : '+');
};

/**
 * Hook to register keyboard shortcuts
 */
/**
 * True when a dialog, menu or listbox layer is open (Radix dialogs/popovers/menus/selects and the
 * hand-rolled modals all carry one of these roles). Escape belongs to that layer, so global
 * "go back" must not also fire and navigate the page underneath away.
 */
export function hasOpenLayer(doc: Document = document): boolean {
  const layers = doc.querySelectorAll<HTMLElement>(
    '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]',
  );
  for (const el of Array.from(layers)) {
    if (el.getAttribute('aria-hidden') === 'true' || el.hidden) continue;
    // jsdom has no layout, so only rule out elements the browser says are not rendered.
    if (typeof el.checkVisibility === 'function' && !el.checkVisibility()) continue;
    return true;
  }
  return false;
}

export const useKeyboardShortcuts = (
  shortcuts: Record<string, KeyboardShortcutDef>,
  enabled = true
): void => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in inputs
    const target = event.target as HTMLElement;
    const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
    const isContentEditable = target.isContentEditable;

    if (isInput || isContentEditable) {
      // Allow some shortcuts even in inputs (like Cmd+K for command palette)
      const allowedInInputs = ['k'];
      if (!allowedInInputs.includes(event.key.toLowerCase())) {
        return;
      }
    }

    // Escape that something else already handled, or that belongs to an open dialog/menu, is not "go back".
    if (event.key === 'Escape' && (event.defaultPrevented || hasOpenLayer())) return;

    // Check each shortcut
    for (const shortcut of Object.values(shortcuts)) {
      if (matchesShortcut(event, shortcut)) {
        event.preventDefault();
        if (shortcut.action) {
          shortcut.action(event);
        }
        break;
      }
    }
  }, [shortcuts, enabled]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

/**
 * Hook to get all registered shortcuts
 */
export const useShortcutsList = (shortcuts: Record<string, KeyboardShortcutDef>): FormattedShortcut[] => {
  return Object.entries(shortcuts).map(([id, shortcut]) => ({
    id,
    ...shortcut,
    formatted: formatShortcut(shortcut)
  }));
};

export default useKeyboardShortcuts;
